import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, User, MessageSquare, Clock, CheckCircle2, Shield, RefreshCw, Plus, Package, Shirt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { conversationsAPI, adminAPI, publicAPI } from '../../lib/api';

export default function ConversationDetailPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedLooks, setSelectedLooks] = useState([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showLookPicker, setShowLookPicker] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableLooks, setAvailableLooks] = useState([]);
  const bottomRef = useRef(null);

  const statusBadge = (status) => {
    const normalized = (status || '').toUpperCase();
    const map = {
      OPEN: { label: 'Ouvert', variant: 'default' },
      ASSIGNED: { label: 'Assigné', variant: 'secondary' },
      CLOSED: { label: 'Clôturé', variant: 'outline' }
    };
    const config = map[normalized] || map.OPEN;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const loadConversation = useCallback(async () => {
    setLoading(true);
    try {
      const res = await conversationsAPI.get(conversationId);
      setConversation(res.data);
      setMessages(res.data.messages || []);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      console.error('Error loading conversation', err);
      navigate('/admin/messages');
    } finally {
      setLoading(false);
    }
  }, [conversationId, navigate]);

  useEffect(() => {
    loadConversation();
    loadProductsAndLooks();
  }, [loadConversation]);

  const loadProductsAndLooks = async () => {
    try {
      const [productsRes, looksRes] = await Promise.all([
        publicAPI.getProducts({ limit: 50 }),
        publicAPI.getLooks({ limit: 50 })
      ]);
      setAvailableProducts(productsRes.data.items || []);
      setAvailableLooks(looksRes.data.items || []);
    } catch (err) {
      console.error('Error loading products/looks', err);
    }
  };

  const isAssignedToMe = useMemo(() => {
    return conversation?.assigned_to === user?.id;
  }, [conversation, user]);

  const isClosed = useMemo(() => (conversation?.status || '').toUpperCase() === 'CLOSED', [conversation]);

  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending || isClosed) return;
    setSending(true);
    try {
      const productIds = selectedProducts.map(p => p.id);
      const lookIds = selectedLooks.map(l => l.id);
      console.log('Sending message:', { content, productIds, lookIds });
      
      const res = await conversationsAPI.sendMessage(
        conversationId,
        content,
        productIds,
        lookIds
      );
      console.log('Message sent successfully:', res.data);
      
      setMessages((prev) => [...prev, res.data]);
      setInput('');
      setSelectedProducts([]);
      setSelectedLooks([]);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      console.error('Error sending message', err);
      alert(`Erreur lors de l'envoi: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleAssign = async () => {
    try {
      const res = await conversationsAPI.assign(conversationId);
      setConversation((prev) => ({
        ...prev,
        assigned_to: user?.id,
        assigned_to_name: res.data.assigned_to,
        status: 'ASSIGNED'
      }));
    } catch (err) {
      console.error('Error assigning conversation', err);
    }
  };

  const handleClose = async () => {
    try {
      await conversationsAPI.close(conversationId);
      setConversation((prev) => ({ ...prev, status: 'CLOSED', closed_at: new Date().toISOString() }));
    } catch (err) {
      console.error('Error closing conversation', err);
    }
  };

  if (loading || !conversation) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/messages')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Retour
        </Button>
        {statusBadge(conversation.status)}
        {conversation.assigned_to_name && (
          <Badge variant="secondary">Assigné à {conversation.assigned_to_name}</Badge>
        )}
      </div>

      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <CardTitle className="text-xl">{conversation.subject || 'Conversation'}</CardTitle>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
              <span className="flex items-center gap-1"><User className="h-4 w-4" /> {conversation.user_name}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {new Date(conversation.created_at).toLocaleString('fr-FR')}</span>
              {conversation.closed_at && (
                <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Clôturé {new Date(conversation.closed_at).toLocaleDateString('fr-FR')}</span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isClosed && !isAssignedToMe && (
              <Button variant="outline" size="sm" onClick={handleAssign}>
                <Shield className="h-4 w-4 mr-1" /> M'assigner
              </Button>
            )}
            {!isClosed && (
              <Button variant="secondary" size="sm" onClick={handleClose}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Clore
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={loadConversation}>
              <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="border rounded-lg">
            <ScrollArea className="h-[60vh] p-4">
              <div className="space-y-4">
                {messages.map((msg) => {
                  const isStaff = (msg.sender_role || '').toUpperCase() !== 'USER';
                  return (
                    <div key={msg.id} className={`flex ${isStaff ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-lg p-3 text-sm border ${isStaff ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-100 text-slate-900'} `}>
                        <div className="flex items-center gap-2 text-xs opacity-80 mb-1">
                          {isStaff ? <Shield className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                          <span>{isStaff ? 'Équipe' : conversation.user_name}</span>
                          <span>•</span>
                          <span>{new Date(msg.created_at).toLocaleString('fr-FR')}</span>
                        </div>
                        <div className="whitespace-pre-line leading-relaxed">{msg.content}</div>
                        {/* Product recommendations */}
                        {msg.products?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {msg.products.map(prod => (
                              <Link
                                key={prod.id}
                                to={`/products/${prod.slug}`}
                                className="flex gap-2 p-2 bg-white/10 hover:bg-white/20 rounded border border-white/20 transition"
                              >
                                {prod.image && (
                                  <img src={prod.image} alt={prod.name} className="w-16 h-16 object-cover rounded" />
                                )}
                                <div className="flex-1 flex flex-col justify-center text-xs">
                                  <p className="font-medium">{prod.name}</p>
                                  {prod.price_cents > 0 && (
                                    <p className="text-white/75">{(prod.price_cents / 100).toFixed(2)}€</p>
                                  )}
                                  <p className="text-white/50 mt-1">Voir le produit →</p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                        {/* Look recommendations */}
                        {msg.looks?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {msg.looks.map(look => (
                              <Link
                                key={look.id}
                                to={`/looks/${look.slug}`}
                                className="flex gap-2 p-2 bg-white/10 hover:bg-white/20 rounded border border-white/20 transition"
                              >
                                {look.image && (
                                  <img src={look.image} alt={look.name} className="w-16 h-16 object-cover rounded" />
                                )}
                                <div className="flex-1 flex flex-col justify-center text-xs">
                                  <p className="font-medium">{look.name}</p>
                                  {look.bundle_price_cents > 0 && (
                                    <p className="text-white/75">{(look.bundle_price_cents / 100).toFixed(2)}€</p>
                                  )}
                                  <p className="text-white/50 mt-1">Voir le look →</p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
            </ScrollArea>

            <div className="border-t p-3 space-y-2">
              {/* Selected items preview */}
              {(selectedProducts.length > 0 || selectedLooks.length > 0) && (
                <div className="flex flex-wrap gap-2 pb-2 border-b">
                  {selectedProducts.map(p => (
                    <Badge key={p.id} variant="secondary" className="gap-1">
                      <Package className="h-3 w-3" /> {p.name}
                      <button onClick={() => setSelectedProducts(prev => prev.filter(x => x.id !== p.id))} className="ml-1">×</button>
                    </Badge>
                  ))}
                  {selectedLooks.map(l => (
                    <Badge key={l.id} variant="secondary" className="gap-1">
                      <Shirt className="h-3 w-3" /> {l.name}
                      <button onClick={() => setSelectedLooks(prev => prev.filter(x => x.id !== l.id))} className="ml-1">×</button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" disabled={isClosed} onClick={() => setShowProductPicker(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Produit
                </Button>

                <Dialog open={showProductPicker} onOpenChange={setShowProductPicker}>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Recommander des produits</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                      {availableProducts.map(product => (
                        <div
                          key={product.id}
                          onClick={() => {
                            if (!selectedProducts.find(p => p.id === product.id)) {
                              setSelectedProducts(prev => [...prev, product]);
                            }
                            setShowProductPicker(false);
                          }}
                          className="border rounded p-3 cursor-pointer hover:bg-muted/50 transition"
                        >
                          <p className="font-medium text-sm">{product.name}</p>
                          <p className="text-xs text-muted-foreground">{product.category}</p>
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>

                <Button type="button" variant="outline" size="sm" disabled={isClosed} onClick={() => setShowLookPicker(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Look
                </Button>

                <Dialog open={showLookPicker} onOpenChange={setShowLookPicker}>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Recommander des looks</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                      {availableLooks.map(look => (
                        <div
                          key={look.id}
                          onClick={() => {
                            if (!selectedLooks.find(l => l.id === look.id)) {
                              setSelectedLooks(prev => [...prev, look]);
                            }
                            setShowLookPicker(false);
                          }}
                          className="border rounded p-3 cursor-pointer hover:bg-muted/50 transition"
                        >
                          <p className="font-medium text-sm">{look.name}</p>
                          <p className="text-xs text-muted-foreground">{look.style || look.occasion}</p>
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>

                <Input
                  placeholder={isClosed ? 'Conversation clôturée' : 'Écrire une réponse...'}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={sending || isClosed}
                  className="flex-1"
                />
                <Button onClick={handleSend} disabled={sending || isClosed || !input.trim()}>
                  <Send className="h-4 w-4 mr-1" /> Envoyer
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
